import clonedeep from 'lodash.clonedeep';
import { getActiveFiltersWithValues } from './active-filters';
import querystringWhitelist from '../constants/querystring-whitelist';
import { QUERY_PARAM_SORT, QUERY_PARAM_PAGE, QUERY_PARAM_MODAL, QUERY_PARAM_LOCATION } from '../constants/filter-keys';
import getFilterKeys from './filters/filter-helpers';
import validateAgainstWhitelist from './querystring-whitelist-helpers';
/**
 * Return a new String of of url query params
 *
 * @returns {String} Returns - String of query params for current page url
 *
 * Example usage:
 * const queryString = getQueryString;
 *
 */
export function getQueryString() {
    return window.location.search.substring(1);
}

/**
 * Return a new array of strings
 *
 * @param {Array} queryStringParams - Array of queryString key/value pairs
 * @returns {Array} Returns - New array of strings
 *
 * Example usage:
 * const vars = url.split('&');
 * const qsKys = getQueryStringKeys(vars);
 *
 */
function getQueryStringKeys(queryStringParams) {
    const keys = [];

    queryStringParams.forEach(key => {
        const k = key.replace(/=(.+)/, '');
        keys.push(k);
    });

    return keys;
}

/**
 * Return a new array of invalid strings
 *
 * @param {Object} filters - object of available filters
 * @param {string} urlParams - Optional string of url query params (used for testing purposes)
 * @returns {Array} Returns - Array containing strings of invalid url params
 *
 * Example usage:
 * let filterString = persistQueryString(state.activeFilters);
 *
 */
export function persistQueryString(filters, urlParams = window.location.search.substring(1)) {
    const queryString = urlParams.split('&');
    const urlKeys = getQueryStringKeys(queryString);
    const filterKeys = getFilterKeys(filters);
    const whitelist = [...querystringWhitelist, ...filterKeys];
    // filter out the invalid key/value pairs from the whitelist and add them to filterString
    if (urlKeys[0] !== '') {
        const invalidKeys = urlKeys.filter(item => !whitelist.includes(item));
        // get the array items from queryString that match the invalid keys
        // assign them to filterString
        return queryString.filter(item => {
            const found = invalidKeys.find(key => item.startsWith(`${key}=`));
            return found !== undefined;
        });
    }

    return [];
}

export function parseQueryString(queryString, filters, pageNum = 0) {
    // Default (empty) object of possible filters
    const obj = {
        activeFilters: clonedeep(filters),
        currentPage: pageNum,
        currentSort: 'default'
    };


    // Return default object if query string is not set
    if (!queryString) { return obj; }

    // Map the variables to an object
    const variables = queryString.split('&');

    const varKeys = getQueryStringKeys(variables);
    const filterKeys = getFilterKeys(filters);
    const whitelist = [...querystringWhitelist, ...filterKeys];
    const hasValidParams = validateAgainstWhitelist(whitelist, varKeys);

    // If the url does NOT have params in our whitelist, return null
    // prevents updating the url
    if (!hasValidParams) return null;

    variables.forEach(variable => {
        const arr = variable.split('=');
        const key = arr[0];
        const options = arr[1].split(',');

        const isValidKey = whitelist.includes(key);

        // Only parse the string if key is in our whitelist AND we have options
        if (!isValidKey && (!options.length || !options[0])) { return; }
        if (key === QUERY_PARAM_SORT) {
            // Simple single value options
            obj[key] = decodeURIComponent(options[0]);
        } else if (key === QUERY_PARAM_PAGE) {
            if (/\d$/gi.test(options[0])) {
                // If we have only digits
                obj[key] = options[0]; // eslint-disable-line prefer-destructuring
            }
        } else if ((key === QUERY_PARAM_MODAL) || (key === QUERY_PARAM_LOCATION)) {
            obj[key] = options; // eslint-disable-line prefer-destructuring
        } else {
            // Parse active filters
            const currentFilters = decodeURIComponent(options[0]).split('+');

            obj.activeFilters.forEach(filter => {
                if (filter.queryParam === key) {
                    currentFilters.forEach(current => {
                        if (current === 'true') {
                            filter.values.push(true);
                        } else if (current === 'false') {
                            filter.values.push(false);
                        } else {
                            filter.values.push(current);
                        }
                    });
                }
            });
        }
    });

    return obj;
}

// Build the query string from the object provided
export function buildQueryString(value, url = window.location.search.substring(1)) {
    // If we're passed null we assume filters are being cleared
    if (!value) { return ''; }

    // Map the value from a complicated object
    let filterString = [];

    Object.keys(value).forEach(key => {
        // Handle the selectedFilters key differently to nomal values (just like in parseQueryString())
        if (key === 'activeFilters') {
            const selectedFilters = getActiveFiltersWithValues(value.activeFilters);
            selectedFilters.forEach(filter => {
                const opts = filter.values.map(opt => encodeURIComponent(opt));
                filterString.push(`${filter.queryParam}=${opts.join('+')}`);
            });
        } else if ((key === QUERY_PARAM_SORT && value[key] !== 'default')) {
            filterString.push(`${key}=${encodeURIComponent(value[key])}`);
        } else if (key === QUERY_PARAM_PAGE && value[key] !== 0) {
            filterString.push(`${key}=${value[key]}`);
        } else if (key === 'modalDataItems' && value[key]) {
            filterString.push(`${QUERY_PARAM_LOCATION}=${value[key].guid}`);
            filterString.push(`${QUERY_PARAM_MODAL}=true`);
        }
    });

    // append non filterable query params to end of built url string
    const invalidParams = persistQueryString(value.activeFilters, url);
    filterString = [...filterString, ...invalidParams];

    return filterString.length ? `?${filterString.join('&')}` : '';
}
